/**
 * Frontend Google Places client — talks to the Google Maps JS SDK directly
 * from the browser using the iframe's own key (`VITE_GOOGLE_MAP_API_KEY`).
 *
 * Why the JS SDK and not the REST endpoints: the Places REST API
 * (`maps.googleapis.com/maps/api/place/*`) does NOT send CORS headers, so a
 * browser fetch is blocked — that's the whole reason the web project proxies
 * it server-side. The JS SDK is the browser-native path and needs no backend.
 *
 * The minimal `google.maps.places` typings below are inlined so we don't pull
 * in the full `@types/google.maps` dependency.
 */

export interface AddressPrediction {
  placeId: string;
  description: string;
}

/** One Google `address_component` (snake_case — same shape both the SDK and
 *  the REST API return, so `parseAddressDetails` consumes it verbatim). */
export interface GoogleAddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

export interface PlaceDetailsResult {
  address_components?: GoogleAddressComponent[];
}

interface GooglePrediction {
  place_id: string;
  description: string;
}
interface GooglePlaceResult {
  address_components?: GoogleAddressComponent[];
}
interface GooglePlacesNamespace {
  AutocompleteService: new () => {
    getPlacePredictions(
      request: { input: string },
      callback: (predictions: GooglePrediction[] | null, status: string) => void,
    ): void;
  };
  PlacesService: new (attrContainer: HTMLElement) => {
    getDetails(
      request: { placeId: string; fields: string[] },
      callback: (place: GooglePlaceResult | null, status: string) => void,
    ): void;
  };
  PlacesServiceStatus: { OK: string };
}
interface GoogleMapsNamespace {
  places: GooglePlacesNamespace;
}

declare global {
  interface Window {
    google?: { maps?: GoogleMapsNamespace };
  }
}

const API_KEY = import.meta.env.VITE_GOOGLE_MAP_API_KEY as string | undefined;

let loadPromise: Promise<GoogleMapsNamespace | null> | null = null;

/** Lazily inject the Google Maps JS SDK (Places library) exactly once.
 *  Resolves null when the key is missing or the script fails — callers then
 *  degrade to "no suggestions" rather than throwing. */
function loadMaps(): Promise<GoogleMapsNamespace | null> {
  if (!API_KEY) return Promise.resolve(null);
  if (window.google?.maps?.places) return Promise.resolve(window.google.maps);
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve) => {
    const script = document.createElement('script');
    script.src =
      `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(API_KEY)}` +
      '&libraries=places&loading=async';
    script.async = true;
    script.onload = () => resolve(window.google?.maps ?? null);
    script.onerror = () => resolve(null);
    document.head.appendChild(script);
  });
  return loadPromise;
}

/** True when an API key is present — lets the form hide the autocomplete
 *  affordance entirely when no key is configured. */
export const hasPlacesKey = Boolean(API_KEY);

/** Address predictions for a free-text query, straight from the browser. */
export async function placesAutocomplete(input: string): Promise<AddressPrediction[]> {
  if (!input.trim()) return [];
  const maps = await loadMaps();
  if (!maps) return [];

  const service = new maps.places.AutocompleteService();
  return new Promise((resolve) => {
    service.getPlacePredictions({ input }, (predictions, status) => {
      if (status !== maps.places.PlacesServiceStatus.OK || !predictions) {
        resolve([]);
        return;
      }
      resolve(
        predictions
          .filter((p) => p.place_id)
          .map((p) => ({ placeId: p.place_id, description: p.description })),
      );
    });
  });
}

/** Full address components for a chosen prediction. */
export async function placeDetails(placeId: string): Promise<PlaceDetailsResult | null> {
  if (!placeId) return null;
  const maps = await loadMaps();
  if (!maps) return null;

  const service = new maps.places.PlacesService(document.createElement('div'));
  return new Promise((resolve) => {
    service.getDetails({ placeId, fields: ['address_components'] }, (place, status) => {
      if (status !== maps.places.PlacesServiceStatus.OK || !place) {
        resolve(null);
        return;
      }
      resolve({ address_components: place.address_components ?? [] });
    });
  });
}
